import once from 'lodash.once';

import { TimeframeOptions } from '@/helpers/fetchAndDownloadLeadsExport';
import { UserGroup } from '@/types';

export type GTMEventCallback = () => void;

export type TrackingParameterValue = string | number | boolean | undefined | GTMEventCallback;

export interface TrackingEvent {
  eventCallback?: GTMEventCallback;
  eventTimeout?: number;
  [key: string]: TrackingParameterValue;
}

export interface PageTrackingParameters {
  client_login_status?: LoginStatus;
  client_login_userId?: string;
  client_login_userType?: UserGroup;
  page_contentLanguage?: string;
  page_subType?: string;
  page_type?: GTMPageType;
  tech_featureId?: GTMPageFeatureId;
  page_normalizedUrl?: string;
  page_realUrl?: string;
  listing_insertion_suggestion?: string;
  listing_id?: string;
  listing_draftId?: string;
  listing_propertyReferenceId?: string;
  listing_lister_id?: string;
  listing_zip?: string;
  listing_canton?: string;
  listing_country?: string;
  listing_geo_tags?: string;
  listing_offerType?: string;
  listing_categories?: string;
  listing_pictureCount?: number;
  listing_status?: string;
  product_matchingId?: string; // listing bundle ID
  list_name?: string;
  list_items_ids_all?: string;
  list_items_status_all?: string;
  list_items_count_all?: number;
  event?: string;
  experiment_id?: string;
  experiment_variant?: string;
  interaction_actor?: string;
  interaction_containerDetail?: string;
  interaction_containerName?: string;
  interaction_element?: string;
  interaction_error?: string;
  interaction_goal?: string;
  interaction_mode?: string;
  interaction_sysFeedback?: string;
  lead_type?: string;
  advertiser_type?: string;
  eventCallback?: GTMEventCallback;
  eventTimeout?: number;
  ga4_event_name?: string;
  non_interaction?: boolean;
  h_feature_group_001?: string;
  h_event_category_006?: string;
  h_event_action_007?: string;
  h_event_label_008?: string;
  h_web_feature_id_019?: string;
  h_transaction_id_042?: string;
  h_transaction_total_017?: number;
  h_transaction_type_063?: string;
  h_product_matching_id_038?: string;
  h_purchase_list_items_all_173?: string;
  h_listing_advertisement_id_120?: string;
  h_listing_object_ref_id_123?: string;
  h_experiment_variant_array_048?: string;
  h_experiment_split_treatments_107?: string;

  // search parameters
  h_search_sorted_details_073?: string;
  h_search_term_geo_080?: string;
  h_listing_status_121?: string;
  h_search_offer_type_083?: string;
  h_search_filter_category_075?: string;
  h_listing_source_153?: string;
  h_listing_vas_156?: string;
  h_listing_completeness_score_127?: string;

  // is24 parameters
  activeLanguage?: string;
  customerType?: UserGroup;
  englishVirtualPagePath?: string;
  eventAction?: string;
  eventCategory?: string;
  eventLabel?: string;
  listingZipCode?: string;
  offerType?: string;
  userId?: string;
  userLoggedIn?: LoginStatus;

  // ubm parameters
  h_page_type_110?: string;
  h_page_subtype_112?: string;
  h_content_language_116?: string;
  h_client_login_user_type_037?: string;
  h_client_login_user_id_036?: string;
  h_client_agency_id_033?: string;
  h_conversion_source_061?: string;
  h_funnel_step_input_141?: number;
  h_extra_information_144?: string | number;
  h_login_status_035?: string;
  h_login_location_049?: string;
  h_client_login_provider_034?: string;
}

export enum GTMPageFeatureId {
  FUNNEL_PAGE_LOAD = 'p011',
  MY_ADS_PAGE_LOAD = 'p012',
  LANDING_PAGE_LOAD = 'p013',
  LANDING_PAGE_SUBMIT = 'e040',
  LANDING_PAGE_MOBILE_TRANSITION = 'e048',
  // APP_LOAD = 'p011b', // triggered from index.html
  BUNDLE_PRICING_LOAD = 'e018',
  CLICK_EVENT = 'e045',
  BIZHUB_CLICK_EVENT = 'e053',
  BIZHUB_ADS_PAGE_LOAD = 'p014',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  BUNDLE_DETAILS_EVENT = 'e048',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  PURCHASE_EVENT = 'e048',
}

export enum GTMInteractionGoal {
  GO_TO_LISTINGS = 'goTo_listings',
  GO_TO_IS24_LISTINGS = 'goTo_is24_migrated_listings',
  GO_TO_NEW_LISTING = 'goTo_newListing',
  GO_TO_AGENCY = 'goTo_agency',
  GO_TO_MYHOMEGATE = 'goTo_myHomegate',
  GO_TO_MR = 'goTo_marketingReport',
  GO_TO_LOGOUT = 'goTo_logout',
  MANAGE_LISTINGS_EDIT = 'edit',
  MANAGE_LISTINGS_PREVIEW = 'preview',
  IS24_MANAGE_LISTING_PREVIEW = 'is24_migrated_preview',
  MANAGE_LISTINGS_DELETE = 'delete',
  IS24_MANAGE_LISTINGS_DELETE = 'is24_migrated_delete',
  MANAGE_LISTINGS_REACTIVATE = 'reactivate',
  IS24_MANAGE_LISTINGS_REACTIVATE = 'is24_migrated_reactivate',
  MANAGE_LISTINGS_DEACTIVATE = 'deactivate',
  MANAGE_LISTINGS_DUPLICATE = 'duplicate',
  MANAGE_LISTINGS_OPEN_TL_MODAL = 'openTLModal',
  MANAGE_LISTINGS_EDIT_TL = 'editTL',
  MANAGE_LISTINGS_BOOK_TL = 'bookTL',
  MANAGE_LISTINGS_DEACTIVATE_TL = 'deactivateTL',
  SEARCH_SUBMIT = 'submit',
  SEARCH_RESET = 'reset',
  BUNDLE_GET_INFO = 'getInfo',
  BUNDLE_SHOW_GUARANTEE_INFO = 'showGuaranteeInfo',
}

export enum EventAction {
  DISPLAY_TOPLISTING_MODAL = 'display_top_listing_modal',
  DISPLAY_DEACTIVATE_TOPLISTING_MODAL = 'display_deactivate_top_listing_modal',
  GO_BACK = 'go_back',
  SELECT_TOPLISTING_PACKAGE = 'select_top_listing_package',
  BOOK_TOPLISTING = 'book_TL',
  CANCEL_DEACTIVATE_TOPLISTING = 'cancel_deactivate_top_listing',
  CANCEL_ARCHIVING = 'cancel_archiving',
  CANCEL_DELETION = 'cancel_deletion',
  CANCEL_TOPLISTING = 'cancel_TL_booking',
  CLOSE_ARCHIVING = 'close_archiving',
  CLOSE_DELETION = 'close_deletion',
  CLOSE_TOPLISTING = 'close_TL_booking',
  CONFIRM_ARCHIVING = 'confirm_archiving',
  CONFIRM_DELETION = 'confirm_deletion',
  COST_REPORT = 'cost_report',
  DEACTIVATE_TOPLISTING = 'deactivate_TL',
  LISTING_COMPLETENESS = 'listing_completeness',
  LISTING_DETAILS = 'listing_details',
  IS24_LISTING_DETAILS = 'is24_migrated_listing_details',
  LISTING_INSIGHTS = 'listing_insights',
  LISTING_LEADS = 'listing_leads',
  TOTAL_IMPRESSIONS = 'total_impressions',
  TOTAL_LEADS = 'total_leads',
  TOTAL_VIEWS = 'total_views',
  INVITE_USER = 'invite_user',
  SWITCH_AGENCY = 'switch_agency',
  REVOKE_USER = 'revoke_user',
  PARTNER_LIST = 'partner_list',
  GO_TO_LOGIN = 'go_to_login',
  GO_TO_SAAS_FF = 'go_to_saas_ff',
  EDIT_AGENCY_LOGO = 'edit_agency_logo',
  UPLOAD_AGENCY_LOGO = 'upload_agency_logo',
  SAVE_AGENCY_LOGO = 'save_agency_logo',
  SAVE_AGENCY_INFO = 'save_agency_info',
  SAVE_AGENT_SORTING = 'save_agent_sorting',
  ADD_AGENT = 'add_agent',
  EDIT_AGENT = 'edit_agent',
  DELETE_AGENT = 'delete_agent',
  DISPLAY_MODAL_MFA = 'display_modal_mfa',
  CLOSE_MODAL_MFA = 'close_modal_mfa',
  SKIP_MFA = 'skip_mfa',
  ACTIVATE_MFA = 'activate_mfa',
  OPEN_EXPORT_CSV_MODAL = 'open_export_csv_cr',
  OPEN_BULK_PROMOTION_MODAL = 'open_bulk_promotion_modal',
  EXPORT_CSV = 'export_csv_cr',
  CLOSE_EXPORT_CSV_MODAL = 'close_export_csv_cr',
  EXPORT_CSV_SUCCESS = 'successful_download',
  EXPORT_CSV_FAIL = 'failed_download',
  DOWNLOAD_INVOICE = 'download_invoice',
  OPEN_INVOICE = 'open_invoice',
  DISPLAY_API_MESSAGE = 'display_api_message',
  CLOSE_API_MESSAGE = 'close_api_message',
  VIEW_API_DOCS = 'view_api_docs',
  DELETE_ALL_ATTACHMENTS = 'delete_all_attachments',
  SORT_LISTINGS = 'sort_listings',
  OPEN_SORT_LISTINGS = 'open_sort_listings',
  OPEN_VISIBILITY_PLUS_LP = 'open_visibility_plus_lp',
  AVERAGE_SEARCH_RANK = 'average_search_rank',
  SEARCH_POSITION = 'search_position',
  SIMILAR_LISTING = 'similar_listing',
  TOP_10_RESULTS = 'top_10_results',
  PERFORMANCE = 'performance',
  AI_DIAGNOSIS = 'ai_diagnosis',
  VIEW_IAZI_PRICE_CHECK_RENT = 'view_iazi_price_check_rent',
  VIEW_IAZI_PRICE_CHECK_BUY = 'view_iazi_price_check_buy',
  EDIT_PRICE = 'edit_price',
  SHOW_ALL = 'show_all',
}

export enum ConversionSource {
  INSERTION_FLOW = 'insertion_flow',
  BOOST_VISIBILITY = 'boost_visibility',
  EDIT_UPGRADE = 'edit_upgrade',
}

export enum FeatureGroup {
  LISTING_INSIGHTS = 'listing_insights',
}

export enum EventLabel {
  ADD_ADDRESS = 'add_address',
  ADD_DESCRIPTION = 'add_description',
  ADD_FLOOR_PLAN = 'add_floor_plan',
  ADD_IMAGES = 'add_images',
  ADD_PRICE = 'add_price',
  ADD_VIDEO = 'add_video',
  ADD_VIRTUAL_TOUR = 'add_virtual_tour',
  ARCHIVE = 'archive',
  DELETE = 'delete',
  DETAILS = 'details',
  DOWNLOAD_COST_REPORT = 'download_cost_report',
  DOWNLOAD_COSTS = 'download_costs',
  DUPLICATE = 'duplicate',
  EDIT = 'edit',
  EDIT_LISTING = 'edit_listing',
  EDIT_TOP_LISTING = 'edit_top_listing',
  IN_MODAL = 'in_modal',
  INFO = 'info',
  OPEN_MODAL = 'open_modal',
  PREVIEW = 'preview',
  REACTIVATE = 'reactivate',
  TOP_LISTING = 'top_listing',
  VISIT_LISTING_LEADS = 'visit_listing_leads',
  VISIT = 'visit',
  CASAONE = 'casaone',
  CASALEAD = 'casalead',
  IAZI = 'iazi',
  REALMATCH360 = 'realmatch360',
  SKRIBBLE = 'skribble',
  MY_VIVENDA = 'my_vivenda',
  RESIDENTURE = 'residenture',
  EXPORT_CSV = 'export_csv_cr',
  EXPORT_INVOICE = 'export_invoice',
  IAZI_PRICE_CHECK = 'iazi_price_check',
  PREMIUM_TOP_LISTING = 'premium_top_listing',
  LIFETIME_TOP_LISTING = 'lifetime_top_listing',
  NO_ADDITIONAL_VISIBILITY = 'no_additional_visibility',
  TOP_LISTING_ABO = 'top_listing_abo',
  START_DATE = 'start_date',
  END_DATE = 'end_date',
  NEXT_BEST_ACTION = 'next_best_action',
  ACTIVATE_EVENTS_UPDATES = 'activate_events_updates',
  DEACTIVATE_EVENTS_UPDATES = 'deactivate_events_updates',
  VIEW_EVENTS_UPDATES_INFO = 'view_events_updates_info',
  VIEW_SIMILAR_LISTING_INFO = 'view_similar_listing_info',
  VIEW_TOP_LISTING_COST_REPORT = 'view_top_listing_cost_report',
}

export enum PageSubType {
  COST_REPORT = 'cost_report',
  MY_ADS = 'my_ads',
  PERFORMANCE_DETAIL = 'performance_detail',
  LISTING_LEADS = 'listing_leads',
  INPUT_START = 'input_start',
  INPUT_DETAILS = 'input_details',
  INPUT_PROMOTION = 'input_promotion',
  PROSPECTION_MAP = 'prospection_map',
  USER_MANAGEMENT = 'user_management',
  AGENCY_PROFILE = 'agency_profile_page',
  USER_INVITE = 'user_invite',
  PARTNERS = 'partners',
  REFERENCE_LISTINGS = 'reference_listings',
  VISIBILITY_PLUS = 'visibility_plus',
  SELLER_LEADS = 'seller_leads',
  SELLER_LEADS_MANAGE_DISTRICTS = 'seller_leads_manage_districts',
  INVOICES = 'invoices',
  INTELLIGENCE_HUB_LANDING = 'intelligence_hub_landing',
  CRM_UPLOAD_REPORT = 'crm_upload_report',
  PACKAGE_OVERVIEW = 'package_overview',
  LISTING_INSIGHTS_HUB = 'listing_insights_hub',
}

export enum GTMPageType {
  FUNNEL_PAGE = 'account_insertListing',
  MY_ADS_PAGE = 'account_manage',
  LANDING_PAGE = 'insertListing',
  ACCOUNT_HUB = 'account_hub',
}

export enum LoginStatus {
  LOGGED_IN = 'loggedin',
  NOT_LOGGED_IN = 'notLoggedin',
}

export const TimeFrameOptionsForTracking = {
  [TimeframeOptions['ALL']]: 'all',
  [TimeframeOptions['7D']]: 'last_7_days',
  [TimeframeOptions['30D']]: 'last_30_days',
  [TimeframeOptions['3M']]: 'last_3_months',
  [TimeframeOptions['CUSTOM']]: 'custom',
};

export const isGTMEnabled = (): boolean => !!window.dataLayer;

export function trackEvent(eventParams: PageTrackingParameters): void {
  // prepare GTM data
  const event: TrackingEvent = { ...eventParams };

  // callback handling - needs to be fired even if tracking blockers are active
  if (event.eventCallback) {
    // make sure eventCallback is only called once, and never again by any fallback timeout
    const eventCallbackOnce = once(event.eventCallback);

    // this happens when Google Tag Manager is not loaded (e.g. in development without SSR or blocked)
    if (!isGTMEnabled()) {
      eventCallbackOnce();
      return;
    }

    // Extend the event params
    event.eventCallback = eventCallbackOnce;
    event.eventTimeout = event?.eventTimeout ?? 1000;

    // if eventCallback is not executed within timeout the callback is executed manually
    window.setTimeout(eventCallbackOnce, event.eventTimeout + 50);
  }

  if (isGTMEnabled()) {
    window.dataLayer.push(event);
  }
}
